import { Injectable, inject } from '@angular/core';
import {
  Correction,
  CorrectionReportData,
  EvolutionPlan,
  CognitiveDiagnosis,
} from '../models/correction.model';
import { UserPlan } from '../models/user.model';
import { LogService } from './log.service';
import { Banca } from '../components/essay-editor/essay-editor.component';
import { SupabaseService } from './supabase.service';

@Injectable({
  providedIn: 'root',
})
export class AiService {
  private logService = inject(LogService);
  private supabaseService = inject(SupabaseService);
  private supabase = this.supabaseService.client;
  private readonly functionTimeoutMs = 90000;
  private readonly tokenRefreshWindowMs = 60000;

  private withTimeout<T>(promise: Promise<T>, timeoutMs: number, operation: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Tempo limite na operação "${operation}" após ${timeoutMs}ms.`));
      }, timeoutMs);

      promise
        .then((value) => {
          clearTimeout(timeout);
          resolve(value);
        })
        .catch((error) => {
          clearTimeout(timeout);
          reject(error);
        });
    });
  }

  private normalizeErrorMessage(raw: unknown): string {
    if (typeof raw === 'string' && raw.trim()) return raw;

    if (raw && typeof raw === 'object') {
      const payload = raw as Record<string, unknown>;
      if (typeof payload.error === 'string' && payload.error.trim()) return payload.error;
      if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
    }

    return 'Falha ao processar a requisição de IA.';
  }

  private async readResponsePayload(response: Response): Promise<unknown> {
    try {
      return await response.json();
    } catch {
      try {
        return await response.text();
      } catch {
        return null;
      }
    }
  }

  private decodeTokenExpiration(token: string): number | null {
    const chunks = token.split('.');
    if (chunks.length < 2) return null;

    try {
      const payloadBase64 = chunks[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(payloadBase64);
      const payload = JSON.parse(json) as { exp?: number };
      if (typeof payload.exp !== 'number') return null;
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  private shouldRefreshToken(token: string): boolean {
    const expiresAt = this.decodeTokenExpiration(token);
    if (!expiresAt) return false;
    return expiresAt <= Date.now() + this.tokenRefreshWindowMs;
  }

  private async refreshAndGetToken(): Promise<string> {
    const { data, error } = await this.supabase.auth.refreshSession();
    if (error) {
      throw new Error(`Falha ao renovar sessão: ${error.message}`);
    }

    const refreshedToken = data.session?.access_token?.trim();
    if (!refreshedToken) {
      throw new Error('Sessão inválida. Faça login novamente.');
    }

    return refreshedToken;
  }

  private async getAccessToken(forceRefresh = false): Promise<string> {
    if (forceRefresh) {
      return this.refreshAndGetToken();
    }

    const { data, error } = await this.supabase.auth.getSession();
    if (error) {
      const errorMessage = String(error.message ?? '').toLowerCase();
      const isLockError =
        errorMessage.includes('navigatorlockacquiretimeouterror')
        || (errorMessage.includes('lock') && errorMessage.includes('acquire'));

      if (isLockError) {
        return this.refreshAndGetToken();
      }
      throw new Error(`Falha ao validar sessão: ${error.message}`);
    }

    const sessionToken = data.session?.access_token?.trim();
    if (!sessionToken) {
      throw new Error('Você precisa estar logado para usar a IA.');
    }

    let token = sessionToken;
    if (this.shouldRefreshToken(token)) {
      token = await this.refreshAndGetToken();
    }

    const { data: validatedUser, error: validateError } = await this.supabase.auth.getUser(token);
    if (validateError || !validatedUser?.user) {
      token = await this.refreshAndGetToken();
    }

    return token;
  }

  private async invokeFunction<T>(
    fn: 'generate-correction' | 'generate-evolution-plan',
    body: unknown,
    accessToken: string
  ): Promise<T> {
    const normalizedToken = accessToken.replace(/^Bearer\s+/i, '').trim();
    if (!normalizedToken) {
      throw new Error('Sessão inválida. Faça login novamente.');
    }

    const anonKey = this.supabaseService.supabaseKey;

    const response = await fetch(`${this.supabaseService.supabaseUrl}/functions/v1/${fn}`, {
      method: 'POST',
      headers: {
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
        'x-user-authorization': `Bearer ${normalizedToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      cache: 'no-store',
    });

    const payload = await this.readResponsePayload(response);
    if (!response.ok) {
      const message = this.normalizeErrorMessage(payload);
      if (response.status === 401) {
        throw new Error(message || 'Unauthorized request.');
      }
      throw new Error(message || `Falha na função ${fn} (${response.status}).`);
    }

    return payload as T;
  }

  private async invokeWithAuthRetry<T>(
    fn: 'generate-correction' | 'generate-evolution-plan',
    body: unknown
  ): Promise<T> {
    const firstToken = await this.getAccessToken(false);

    try {
      return await this.withTimeout(
        this.invokeFunction<T>(fn, body, firstToken),
        this.functionTimeoutMs,
        fn
      );
    } catch (error: any) {
      const message = String(error?.message ?? '').toLowerCase();
      const unauthorized =
        message.includes('unauthorized')
        || message.includes('invalid jwt')
        || message.includes('sessão expirada');
      if (!unauthorized) {
        throw error;
      }

      const refreshedToken = await this.getAccessToken(true);
      try {
        return await this.withTimeout(
          this.invokeFunction<T>(fn, body, refreshedToken),
          this.functionTimeoutMs,
          `${fn} (retry)`
        );
      } catch (retryError: any) {
        const retryMessage = String(retryError?.message ?? '').toLowerCase();
        const retryUnauthorized =
          retryMessage.includes('unauthorized')
          || retryMessage.includes('invalid jwt')
          || retryMessage.includes('sessão expirada');
        if (retryUnauthorized) {
          throw new Error('Sessão expirada. Faça login novamente e tente de novo.');
        }
        throw retryError;
      }
    }
  }

  async correctEssay(
    essayText: string,
    userPlan: UserPlan,
    isProTrial: boolean,
    historicalDiagnoses: CognitiveDiagnosis[] = [],
    banca: Banca
  ): Promise<CorrectionReportData> {
    try {
      const data = await this.invokeWithAuthRetry<CorrectionReportData>('generate-correction', {
        essayText,
        userPlan,
        isProTrial,
        historicalDiagnoses,
        banca,
      });

      if (!data) {
        throw new Error('Resposta vazia da função de correção.');
      }

      return data;
    } catch (error: any) {
      void this.logService.logError('AI correction failed', error, {
        domain: 'essay',
        event: 'essay.correction.invocation_failed',
        status: 'error',
      });
      throw new Error(error.message || 'Falha ao gerar correção com IA.');
    }
  }

  async generateEvolutionPlan(history: Correction[]): Promise<EvolutionPlan> {
    try {
      const data = await this.invokeWithAuthRetry<EvolutionPlan>('generate-evolution-plan', { history });

      if (!data) {
        throw new Error('Resposta vazia da função de plano de evolução.');
      }

      return data;
    } catch (error: any) {
      void this.logService.logError('AI evolution plan failed', error, {
        domain: 'essay',
        event: 'essay.evolution_plan.invocation_failed',
        status: 'error',
      });
      throw new Error(error.message || 'Falha ao gerar plano de evolução com IA.');
    }
  }
}
